import Edit from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import { useSelectedTournament } from '../../contexts/SelectedTournamentContext';
import useTenant from '../../hooks/useTenant';
import { userLogout } from '../../services/UserService';
import { InboxWidget } from './InboxWidget';
import { NavButton } from './NavButton';
import { isFeatureEnabled } from '../../featureFlags';

const NavBar: React.FC = () => {
  const { tId } = useTenant();
  const { apiKey, userProfile, clearApiKey, isAdmin } = useApiKey();
  const { selectedTournament } = useSelectedTournament();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleUsersClick = () => {
    navigate('/users');
    handleMenuClose();
  };

  const handleTeamsClick = () => {
    navigate('/teams');
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    userLogout(apiKey);
    clearApiKey();
    handleMenuClose();
    navigate('/');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <AppBar id={'navbar'} position="static" style={{ background: 'linear-gradient(70deg,#3b87bd,#2d71a2)' }}>
      <Toolbar>
        <Box
          component={Link}
          to={'/'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            marginY: 1.25,
          }}
        >
          <img src={require(`../../assets/logo/${tId}.webp`)} alt="Logo turnieju" style={{ maxWidth: 300, maxHeight: 100 }} />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {userProfile ? (
          <>
            {isFeatureEnabled('messaging') && <InboxWidget />}
            <IconButton onClick={handleMenuOpen}>
              <Avatar alt="User Profile">
                <PersonIcon sx={{ color: 'white' }} />
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              {userProfile.name && (
                <Typography textAlign={'end'} sx={{ px: '16px', py: '4px' }} variant="body2">
                  {`${userProfile.name} ${userProfile.surname}`}
                </Typography>
              )}
              <MenuItem onClick={handleProfileClick}>Profil</MenuItem>
              <MenuItem onClick={handleUsersClick}>Użytkownicy</MenuItem>
              <MenuItem onClick={handleTeamsClick}>Drużyny</MenuItem>
              <MenuItem onClick={handleLogoutClick}>Wyloguj</MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleLoginClick}
            sx={{
              backgroundColor: 'white',
              color: '#3b87bd',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Zaloguj
          </Button>
        )}
      </Toolbar>
      {selectedTournament && (
        <Toolbar style={{ minHeight: 'unset' }}>
          <Grid2 container justifyContent={'space-between'} alignItems={'center'} sx={{ paddingBottom: 0.5 }}>
            <Grid2>
              <Typography variant="h6" display={'inline'}>
                {selectedTournament.name}
              </Typography>
              {isAdmin && (
                <Tooltip arrow title="Edytuj turniej">
                  <IconButton
                    size="small"
                    sx={{
                      pl: 1,
                      color: 'white',
                      '&:hover': {
                        color: '#ffeb3b',
                      },
                    }}
                    onClick={() => navigate(`/edit-tournament/${selectedTournament.id}`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
            </Grid2>
            <Grid2>
              {selectedTournament.id !== 121 && (
                <NavButton
                  label="Tabele"
                  isActive={location.pathname.endsWith('result')}
                  onClick={() => navigate(`/tournament/${selectedTournament.id}/result`)}
                />
              )}
              <NavButton
                label="Mecze"
                isActive={location.pathname.endsWith('schedule')}
                onClick={() => navigate(`/tournament/${selectedTournament.id}/schedule`)}
              />
              <NavButton
                label="Regulamin"
                isActive={location.pathname.endsWith('regulations')}
                onClick={() => navigate(`/tournament/${selectedTournament.id}/regulations`)}
              />
            </Grid2>
          </Grid2>
        </Toolbar>
      )}
    </AppBar>
  );
};

export default NavBar;
